// Run modal for the Striping Plan (STP) task card: what the run will do with each road
// (reuse earlier PSD results, or run PSD and bill it as PSD) and three per-run settings.
// Opened when the card is selected; the card's "Striping plan" chip reopens it. The run
// re-checks every road at submit and wins, so a failed or not-yet-possible check never
// blocks OK — only invalid settings and a road the backend would refuse do.
// Spec: docs/superpowers/specs/2026-10-14-striping-plan-gui-design.md, Part C.
//
// Markup follows ImageryUploadModal (shared rsa-modal styling, backdrop closes only when
// both the press and the release land on it).
import React, { useEffect, useState } from "react";
import { backdropCloseHandlers } from "../../../utils/backdropClose";
import { checkStripingRoads } from "../../../services/stripingPlanService";
import {
  STRIPING_SETTING_DEFAULTS,
  STRIPING_SETTING_FIELDS,
  STRIPING_SETTING_MAX,
  stripingSettingError,
  summarizeStripingCheck,
} from "./InternalComponents/stripingPlanData";

const toInputs = (values) =>
  Object.fromEntries(
    STRIPING_SETTING_FIELDS.map(({ key }) => [
      key,
      String(values?.[key] ?? STRIPING_SETTING_DEFAULTS[key]),
    ]),
  );

const errorMessage = (err) =>
  err?.response?.data?.message ||
  err?.response?.data?.error ||
  err?.message ||
  "The check failed";

export default function StripingPlanModal({
  isOpen,
  routeName = "",
  loadCheckRequest,
  saved = null,
  intersectionCount = 0,
  roadIntersectionCounts = null,
  offRoadPointCount = 0,
  onMarkIntersections = null,
  onCancel,
  onConfirm,
}) {
  const [check, setCheck] = useState({ status: "loading" });
  const [inputs, setInputs] = useState(() => toInputs(saved?.values));
  const [baseline, setBaseline] = useState(
    saved?.baseline || STRIPING_SETTING_DEFAULTS,
  );

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    setInputs(toInputs(saved?.values));
    setBaseline(saved?.baseline || STRIPING_SETTING_DEFAULTS);
    setCheck({ status: "loading" });
    (async () => {
      try {
        const body = await loadCheckRequest();
        if (cancelled) return;
        if (!body) {
          setCheck({ status: "no_files" });
          return;
        }
        const data = await checkStripingRoads(body);
        if (cancelled) return;
        setCheck({ status: "ready", data });
        if (!saved) {
          const fromTemplate = { ...STRIPING_SETTING_DEFAULTS, ...(data?.settings || {}) };
          setBaseline(fromTemplate);
          setInputs(toInputs(fromTemplate));
        }
      } catch (err) {
        if (!cancelled) setCheck({ status: "error", message: errorMessage(err) });
      }
    })();
    return () => {
      cancelled = true;
    };
    // Re-check each time the modal opens; the parent passes a fresh loadCheckRequest.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const errors = Object.fromEntries(
    STRIPING_SETTING_FIELDS.map(({ key }) => [key, stripingSettingError(inputs[key])]),
  );
  const summary = check.status === "ready" ? summarizeStripingCheck(check.data) : null;
  const okDisabled =
    Object.values(errors).some(Boolean) || Boolean(summary?.blocking);

  const confirm = () => {
    const values = Object.fromEntries(
      STRIPING_SETTING_FIELDS.map(({ key }) => [key, Number(inputs[key])]),
    );
    onConfirm({ values, baseline });
  };

  const markLink = (roadLabel) =>
    onMarkIntersections ? (
      <button
        type="button"
        className="striping-modal-link"
        onClick={() => onMarkIntersections(roadLabel)}
      >
        Mark intersections →
      </button>
    ) : null;
  const intersectionsCell = (label) => {
    const n = roadIntersectionCounts?.[label];
    if (n == null) return "—";
    if (n > 0) return n;
    return (
      <>
        0 · no intersection pages {markLink(label)}
      </>
    );
  };

  return (
    <div
      className="rsa-modal-overlay imagery-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="striping-modal-title"
      {...backdropCloseHandlers(onCancel)}
    >
      <div className="rsa-modal imagery-modal striping-modal" onClick={(e) => e.stopPropagation()}>
        <div className="task-info-modal-header">
          <h3 id="striping-modal-title" className="task-info-modal-title">
            Striping Plan{routeName ? ` — ${routeName}` : ""}
          </h3>
          <button
            type="button"
            className="task-info-modal-close"
            onClick={onCancel}
            aria-label="Close striping plan"
          >
            <i className="fa-solid fa-xmark" aria-hidden />
          </button>
        </div>

        <div className="imagery-modal-body">
          <p className="imagery-modal-intro">
            The striping plan redraws each road's passing zones from its PSD results and
            forces "No pass" inside every intersection's window. A road is an N lane and a P
            lane with the same lane label and lane position; a lane without its partner is
            skipped.
          </p>

          {check.status === "loading" && (
            <p className="striping-modal-status" role="status">
              <i className="fas fa-spinner fa-spin" aria-hidden /> Checking each road's PSD results…
            </p>
          )}
          {check.status === "no_files" && (
            <p className="striping-modal-status">
              No Striping Plan lanes are picked yet. Pick each road's N and P lanes, then reopen
              this window from the card's "Striping plan" chip to see what PSD will do.
            </p>
          )}
          {check.status === "error" && (
            <p className="imagery-modal-banner is-unknown">
              Couldn't check the roads: {check.message}. The run checks them again when you
              submit, so you can still go ahead.
            </p>
          )}
          {summary && (
            <>
              <table className="striping-modal-table">
                <thead>
                  <tr>
                    <th>Road</th>
                    <th>N</th>
                    <th>P</th>
                    {roadIntersectionCounts && <th>Intersections</th>}
                  </tr>
                </thead>
                <tbody>
                  {summary.rows.map((row) => (
                    <tr key={row.label}>
                      <td>{row.label}</td>
                      <td>{row.n}</td>
                      <td>{row.p}</td>
                      {roadIntersectionCounts && <td>{intersectionsCell(row.label)}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="striping-modal-status">{summary.psdLine}</p>
              {summary.skipped.map((line) => (
                <p key={line} className="imagery-modal-banner is-missing">{line}</p>
              ))}
              {summary.blocking && (
                <p className="imagery-modal-banner is-unknown">{summary.blocking}</p>
              )}
              {offRoadPointCount > 0 && (
                <p className="imagery-modal-banner is-missing">
                  {`${offRoadPointCount} marked ${offRoadPointCount === 1 ? "point isn't" : "points aren't"} within 150 ft of this route's lanes; the striping plan will ignore them.`}
                </p>
              )}
            </>
          )}

          <p className="striping-modal-status">
            {intersectionCount > 0
              ? `${intersectionCount} intersection${intersectionCount === 1 ? "" : "s"} marked for this route.`
              : "No intersections are marked for this route, so the plan will have no intersection pages."}{" "}
            {intersectionCount === 0 && markLink(null)}
          </p>

          <fieldset className="striping-modal-settings">
            <legend>Settings for this run</legend>
            {STRIPING_SETTING_FIELDS.map(({ key, label }) => (
              <div key={key} className="striping-modal-setting">
                <label htmlFor={`striping-setting-${key}`}>{label}</label>
                <input
                  id={`striping-setting-${key}`}
                  type="number"
                  min="0"
                  max={STRIPING_SETTING_MAX}
                  step="any"
                  className="striping-modal-input"
                  value={inputs[key]}
                  onChange={(e) => setInputs((prev) => ({ ...prev, [key]: e.target.value }))}
                  aria-invalid={Boolean(errors[key])}
                  disabled={check.status === "loading" && !saved}
                />
                {errors[key] && <span className="striping-modal-error">{errors[key]}</span>}
              </div>
            ))}
          </fieldset>
        </div>

        <div className="imagery-modal-footer striping-modal-footer">
          <button type="button" className="rsa-wizard-nav-btn" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="rsa-wizard-nav-btn rsa-wizard-nav-btn-primary"
            onClick={confirm}
            disabled={okDisabled}
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
}
